import { useCallback, useEffect } from 'react';
import loadable from '@loadable/component';
import { useDispatch } from 'react-redux';
import { useTracking } from 'react-tracking';

import { Button } from '@simplywallst/ui-core';
import EditSolid from '@simplywallst/ui-core/icons/EditSolid';
import PlusSolid from '@simplywallst/ui-core/icons/PlusSolid';

import {
  LinkedPortfolioFeedbackBanner,
  ManualPortfolioFeedbackBanner,
  TransactionsPortfolioFeedbackBanner,
} from '@/features/portfolio/components/FeedbackBanner';
import BrazeContentCard from '@/components/BrazeContentCard/BrazeContentCard';

import { ROUTE_PORTFOLIO_TRANSACTIONS_EDIT } from '@/constants/routes';

import { isIncompleteManualPortfolio } from '../PortfolioManual/utils';

import type { Holding, Portfolio } from '@/hooks/portfolio/usePortfolio';

import {
  useManualPortfolioContext,
  useManualPortfolioTracking,
} from '../PortfolioManual';
import { usePanelHoldingId } from '../../hooks/usePanelHoldingId';

import { AddTransactionButton } from '../AddTransaction';

import { HoldingsOverviewLayout } from './HoldingsOverviewLayout';
import { usePortfolioType } from '../HoldingsTable/hooks';

const HoldingsTable = loadable(() => import('../HoldingsTable'), {
  resolveComponent: (c) => c.HoldingsTable,
  fallback: <div className="h-[calc(100vh-112px)]" />, // Height is acquivalent to the height of the side panel
});

const PortfolioPanel = loadable(() => import('../PortfolioPanel'), {
  resolveComponent: (c) => c.PortfolioPanel,
});

type Props = {
  portfolio: Portfolio;
  excludedHoldings?: Holding[];
  sortedHoldings: Holding[];
};

export const HoldingsOverview = ({
  portfolio,
  excludedHoldings = [],
  sortedHoldings,
}: Props) => {
  const dispatch = useDispatch();
  const { trackEvent } = useTracking();

  const defaultHoldingId = sortedHoldings[0]?.id ?? null;

  const { updatePortfolio } = useManualPortfolioContext();
  const { trackManualPortfolioEvent } = useManualPortfolioTracking();

  const handleUpdatePortfolio = () => {
    trackManualPortfolioEvent({
      modifier: 'edit-holdings',
      action: 'click',
      portfolioId: portfolio.id,
    });
    updatePortfolio({ portfolioId: portfolio.id });
  };

  const [panelHoldingId, setPanelHoldingId] = usePanelHoldingId({
    defaultId: defaultHoldingId,
  });

  const handleToggleTableExpansion = useCallback(() => {
    setPanelHoldingId((id) => (id ? null : defaultHoldingId));
  }, [defaultHoldingId, setPanelHoldingId]);

  useEffect(() => {
    if (
      portfolio.type === 'manual' &&
      isIncompleteManualPortfolio(sortedHoldings).incomplete
    ) {
      setPanelHoldingId(null);
    }
  }, [portfolio.id, sortedHoldings, portfolio.type, setPanelHoldingId]);

  const type = usePortfolioType(portfolio);

  return (
    <HoldingsOverviewLayout
      portfolio={portfolio}
      panelHoldingId={panelHoldingId}
      onToggleTableExpansion={handleToggleTableExpansion}
      onUpdatePortfolio={handleUpdatePortfolio}
      main={
        <>
          <HoldingsTable
            portfolioId={portfolio.id}
            excludedHoldings={excludedHoldings}
            holdings={sortedHoldings}
            holdingId={panelHoldingId}
            onHoldingSelect={setPanelHoldingId}
          />
          <div className="grid gap-inherit px-x2 lg:px-0">
            {type === 'manual-portfolio' && (
              <div data-cy-id="holdings-table-footer">
                <Button
                  styleType="gentleLarge"
                  displayXsDown="block"
                  onClick={handleUpdatePortfolio}
                >
                  <EditSolid style={{ marginRight: 2 }} /> Edit holdings
                </Button>
              </div>
            )}
            {type === 'transaction-portfolio' && (
              <div
                data-cy-id="holdings-table-footer"
                className="grid grid-cols-[max-content_max-content] gap-3"
              >
                <AddTransactionButton portfolioId={portfolio.id}>
                  <PlusSolid />
                  Add transaction
                </AddTransactionButton>
                <Button
                  styleType="loudLarge"
                  displayXsDown="block"
                  onClick={() => {
                    trackEvent({
                      modifier: 'editTransactions',
                      action: 'click',
                      portfolioId: portfolio.id,
                    });
                    dispatch({
                      type: ROUTE_PORTFOLIO_TRANSACTIONS_EDIT,
                      payload: { portfolioId: portfolio.id },
                    });
                  }}
                >
                  <EditSolid style={{ marginRight: 2 }} /> Edit all transactions
                </Button>
              </div>
            )}
            <BrazeContentCard
              feedType="new-portfolio.bottom"
              anchorTarget="_blank"
            />
            <div className="pt-x1">
              {type === 'linked-portfolio' ? (
                <LinkedPortfolioFeedbackBanner />
              ) : type === 'transaction-portfolio' ? (
                <TransactionsPortfolioFeedbackBanner />
              ) : (
                <ManualPortfolioFeedbackBanner />
              )}
            </div>
          </div>
        </>
      }
      aside={
        panelHoldingId ? (
          <PortfolioPanel
            holdingId={panelHoldingId}
            excludedHoldings={excludedHoldings}
            holdings={sortedHoldings}
            setHoldingId={setPanelHoldingId}
          />
        ) : null
      }
    />
  );
};
